import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useAuth } from '../services/authProvider'
import { getDayNameTHByIndex, formatTimeShort } from '../utils/teachingUtils'

const fmtDateTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'

const fmtDays = (n) => {
  const v = parseFloat(n)
  if (Number.isNaN(v)) return '-'
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

const todayLocal = () => new Date().toLocaleDateString('en-CA')

const slotKey = (s) => `${s.teach_id}-${s.period_id}`

// สี Badge ตามประเภทการไม่อยู่
const TYPE_VARIANT = { leave: 'info', offsite: 'warning', training: 'primary' }

// บรรทัดรายละเอียดของการไม่อยู่แต่ละประเภท (ช่วงวัน/เวลา · สถานที่ · เหตุผล)
const AbsenceDetail = ({ t }) => (
  <div className="text-muted fs-sm">
    <Badge bg={TYPE_VARIANT[t.absence_type] || 'secondary'} className="me-1">{t.type_label || t.lt_name}</Badge>
    {t.absence_type === 'offsite'
      ? <>{t.time_out} - {t.time_return} น.</>
      : <>{fmtDateTH(t.start_date || t.lr_start_date)} - {fmtDateTH(t.end_date || t.lr_end_date)}{t.days != null && <> ({fmtDays(t.days)} วัน)</>}</>}
    {t.period_start != null && <> · <b>คาบ {t.period_start}{t.period_end !== t.period_start && <>-{t.period_end}</>}</b></>}
    {t.location && <> · <i className="ri-map-pin-line" /> {t.location}</>}
    {t.reason && <> · {t.reason}</>}
  </div>
)

// การ์ดคาบสอนของครูที่ลา 1 คาบ พร้อมฟอร์มหาครูสอนแทน สำหรับมุมมองมือถือ
const SlotCard = ({ s, a, canAssign, onFind, onUpdate, onAssign, onCancel }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">คาบ {s.period_order} · {s.c_class ? `${s.c_class}/${s.r_no}` : '-'}</div>
      <span className="fs-xs text-muted">{formatTimeShort(s.period_begin)} - {formatTimeShort(s.period_end)}</span>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <code className="me-1">{s.sub_subid}</code>{s.sub_subname}
    </div>
    <div className="mt-2 pt-2 border-top">
      {s.sub_id ? (
        <div className="d-flex align-items-center justify-content-between gap-2">
          <span className="text-success">
            <i className="ri-check-line me-1" />
            <b>{s.sub_teacher_name}</b>
            {s.sub_teacher_phone && <span className="text-muted ms-1 fs-xs">({s.sub_teacher_phone})</span>}
          </span>
          {canAssign && (
            <Button variant="link" size="sm" className="text-danger p-0" onClick={onCancel}>ยกเลิก</Button>
          )}
        </div>
      ) : !canAssign ? (
        <Badge bg="danger">ยังไม่จัดสอนแทน</Badge>
      ) : !a.open ? (
        <Button variant="outline-primary" size="sm" className="w-100" disabled={a.loading} onClick={onFind}>
          <i className="ri-user-search-line me-1" />
          {a.loading ? 'กำลังค้นหา...' : 'หาครูแทน'}
        </Button>
      ) : (
        <div className="d-flex flex-column gap-2">
          <Form.Select size="sm" value={String(a.selected || '')} onChange={e => onUpdate({ selected: e.target.value })}>
            <option value="">{a.list && a.list.length ? '-- เลือกครูที่ว่างคาบนี้ --' : 'ไม่มีครูว่างในคาบนี้'}</option>
            {(a.list || []).map(tc => (
              <option key={tc.t_id} value={String(tc.t_id)}>{tc.t_name}{tc.t_phone ? ` — ☎ ${tc.t_phone}` : ''}</option>
            ))}
          </Form.Select>
          <Form.Control size="sm" type="text" placeholder="เหตุผล/หมายเหตุ (ไม่บังคับ)"
            value={a.reason || ''} onChange={e => onUpdate({ reason: e.target.value })} />
          <div className="d-flex gap-2">
            <Button variant="primary" size="sm" className="flex-grow-1" disabled={a.saving} onClick={onAssign}>
              {a.saving ? 'กำลังบันทึก...' : 'มอบหมาย'}
            </Button>
            <Button variant="light" size="sm" className="flex-grow-1" onClick={() => onUpdate({ open: false })}>ปิด</Button>
          </div>
        </div>
      )}
    </div>
  </div>
)

// หน้าจัดครูสอนแทนสำหรับฝ่ายการเรียนการสอน (ผู้ดูแลการสอน pos 5 / admin 99)
// สามารถหาครูว่างและมอบหมาย/ยกเลิกการสอนแทนได้ในหน้าเดียว
export default function SubstituteArrangement() {
  const { posIds } = useAuth()
  const canAssign = Array.isArray(posIds) && posIds.some(p => p === 5 || p === 99)

  const [date, setDate] = useState(todayLocal())
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState({}) // lr_id -> bool
  const [assign, setAssign] = useState({})     // slotKey -> { open, loading, list, selected, reason, saving }
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/reports/leave-on-date?date=${date}`)
      if (res.data.success) {
        setTeachers(res.data.data.teachers)
        // เปิดแถวย่อยของทุกคนไว้ก่อนเมื่อโหลดวันใหม่
        setExpanded(Object.fromEntries(res.data.data.teachers.map(t => [t.key, true])))
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => { fetchData() }, [fetchData])

  // เปลี่ยนวันที่แล้วล้างสถานะการจัดสอนแทนที่ค้างอยู่
  useEffect(() => { setAssign({}) }, [date])

  const patchAssign = (key, patch) =>
    setAssign(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }))

  // ----- หาครูที่ว่างในคาบนี้ -----
  const findSubstitutes = async (s) => {
    const key = slotKey(s)
    patchAssign(key, { loading: true, open: true })
    try {
      const res = await axiosInstance.get(
        `/api/getAvailableSubstitutes/${s.teach_id}/${s.period_id}/${date}`
      )
      patchAssign(key, { list: res.data || [], selected: '', reason: '', loading: false, open: true })
    } catch (err) {
      patchAssign(key, { loading: false })
      Swal.fire({ icon: 'error', title: 'ผิดพลาด', text: 'ไม่สามารถโหลดรายชื่อครูที่ว่างได้' })
    }
  }

  // ----- มอบหมายครูสอนแทน -----
  const assignSubstitute = async (s) => {
    const key = slotKey(s)
    const state = assign[key]
    if (!state || !state.selected) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกครู', text: 'กรุณาเลือกครูสอนแทน' })
      return
    }
    patchAssign(key, { saving: true })
    try {
      await axiosInstance.post(`/api/createSubstitution`, {
        teach_id: s.teach_id,
        period_id: s.period_id,
        sub_date: date,
        sub_teacher_t_id: state.selected,
        reason: state.reason || null
      })
      Swal.fire({ icon: 'success', title: 'สำเร็จ', text: 'มอบหมายครูสอนแทนแล้ว', timer: 1400, showConfirmButton: false })
      patchAssign(key, { open: false, saving: false })
      fetchData()
    } catch (err) {
      patchAssign(key, { saving: false })
      Swal.fire({
        icon: 'error',
        title: err.response?.data?.title || 'ผิดพลาด',
        text: err.response?.data?.message || 'ไม่สามารถมอบหมายได้'
      })
    }
  }

  // ----- ยกเลิกการสอนแทน -----
  const cancelSubstitute = (s) => {
    Swal.fire({
      title: 'ยกเลิกการสอนแทน?',
      text: `${s.sub_subid || ''} ${s.sub_subname || ''} คาบ ${s.period_order} — ${s.sub_teacher_name || ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการสอนแทน',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#d33'
    }).then(async (result) => {
      if (!result.isConfirmed) return
      try {
        await axiosInstance.put(`/api/cancelSubstitution`, { sub_id: s.sub_id })
        Swal.fire({ icon: 'success', title: 'ยกเลิกแล้ว', timer: 1200, showConfirmButton: false })
        fetchData()
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'ผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถยกเลิกได้' })
      }
    })
  }

  const dayName = getDayNameTHByIndex(new Date(date).getDay())
  const totalSlots = teachers.reduce((sum, t) => sum + t.schedule.length, 0)
  const coveredSlots = teachers.reduce((sum, t) => sum + t.schedule.filter(s => s.sub_id).length, 0)

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-user-shared-line me-2 text-primary" />
              จัดครูสอนแทน (ครูไม่อยู่)
            </h4>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                size="sm"
                type="date"
                style={{ width: 170 }}
                value={date}
                onChange={e => e.target.value && setDate(e.target.value)}
              />
              <Button size="sm" variant="outline-secondary" onClick={() => setDate(todayLocal())}>
                วันนี้
              </Button>
              <Button as={Link} to="/academic/substitute-overview" size="sm" variant="outline-primary">
                <i className="ri-swap-line me-1" />ภาพรวมสอนแทน
              </Button>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              <Alert variant={teachers.length === 0 ? 'success' : (coveredSlots === totalSlots ? 'success' : 'warning')} className="py-2">
                <b>วัน{dayName}ที่ {fmtDateTH(date)}</b> —{' '}
                {teachers.length === 0
                  ? 'ไม่มีครูลา / ไม่อยู่ในวันนี้'
                  : <>ครูไม่อยู่ {teachers.length} รายการ · คาบสอนที่ได้รับผลกระทบ {totalSlots} คาบ · จัดสอนแทนแล้ว{' '}
                      <b>{coveredSlots}/{totalSlots}</b> คาบ
                      {totalSlots > coveredSlots && <> · <span className="text-danger fw-bold">ยังไม่จัด {totalSlots - coveredSlots} คาบ</span></>}
                    </>}
              </Alert>

              {teachers.map(t => {
                const covered = t.schedule.filter(s => s.sub_id).length
                const isOpen = !!expanded[t.key]
                return (
                  <Card className="card-one mb-3" key={t.key}>
                    <Card.Header
                      className="d-flex flex-wrap align-items-center gap-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpanded(ex => ({ ...ex, [t.key]: !ex[t.key] }))}
                    >
                      <i className={`ri-arrow-${isOpen ? 'down' : 'right'}-s-line fs-5`} />
                      <div>
                        <b>{t.t_name}</b>
                        {t.t_phone && <span className="text-muted fs-sm ms-2"><i className="ri-phone-line" /> {t.t_phone}</span>}
                        <AbsenceDetail t={t} />
                      </div>
                      <div className="ms-auto">
                        {t.schedule.length === 0 ? (
                          <Badge bg="secondary">ไม่มีคาบสอนวันนี้</Badge>
                        ) : covered === t.schedule.length ? (
                          <Badge bg="success"><i className="ri-check-double-line me-1" />จัดสอนแทนครบ {covered} คาบ</Badge>
                        ) : (
                          <Badge bg="danger">ยังไม่จัด {t.schedule.length - covered}/{t.schedule.length} คาบ</Badge>
                        )}
                      </div>
                    </Card.Header>
                    {isOpen && t.schedule.length > 0 && (
                      <Card.Body className="p-0">
                        {view === 'card' ? (
                          <div className="p-3">
                            <Row className="g-3">
                              {t.schedule.map((s, i) => {
                                const key = slotKey(s)
                                const a = assign[key] || {}
                                return (
                                  <Col key={`${s.teach_id}-${s.period_id}-${i}`} xs={12} md={6} xl={4}>
                                    <SlotCard s={s} a={a} canAssign={canAssign}
                                      onFind={() => findSubstitutes(s)}
                                      onUpdate={(patch) => patchAssign(key, patch)}
                                      onAssign={() => assignSubstitute(s)}
                                      onCancel={() => cancelSubstitute(s)} />
                                  </Col>
                                )
                              })}
                            </Row>
                          </div>
                        ) : (
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 130 }}>คาบ</th>
                              <th>วิชา</th>
                              <th style={{ width: 120 }}>ห้องเรียน</th>
                              <th style={{ width: canAssign ? 320 : 240 }}>การสอนแทน</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.schedule.map((s, i) => {
                              const key = slotKey(s)
                              const a = assign[key] || {}
                              return (
                                <tr key={`${s.teach_id}-${s.period_id}-${i}`}>
                                  <td>
                                    คาบ {s.period_order}
                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      {formatTimeShort(s.period_begin)} - {formatTimeShort(s.period_end)}
                                    </div>
                                  </td>
                                  <td>
                                    <code className="me-1">{s.sub_subid}</code>{s.sub_subname}
                                  </td>
                                  <td>{s.c_class ? `${s.c_class}/${s.r_no}` : '-'}</td>
                                  <td>
                                    {s.sub_id ? (
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="text-success">
                                          <i className="ri-check-line me-1" />
                                          <b>{s.sub_teacher_name}</b>
                                          {s.sub_teacher_phone && (
                                            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>({s.sub_teacher_phone})</span>
                                          )}
                                        </span>
                                        {canAssign && (
                                          <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0 ms-1"
                                            onClick={() => cancelSubstitute(s)}
                                          >
                                            ยกเลิก
                                          </Button>
                                        )}
                                      </div>
                                    ) : !canAssign ? (
                                      <Badge bg="danger">ยังไม่จัดสอนแทน</Badge>
                                    ) : !a.open ? (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={a.loading}
                                        onClick={() => findSubstitutes(s)}
                                      >
                                        <i className="ri-user-search-line me-1" />
                                        {a.loading ? 'กำลังค้นหา...' : 'หาครูแทน'}
                                      </Button>
                                    ) : (
                                      <div className="d-flex flex-column gap-2" style={{ maxWidth: 300 }}>
                                        <Form.Select
                                          size="sm"
                                          value={String(a.selected || '')}
                                          onChange={e => patchAssign(key, { selected: e.target.value })}
                                        >
                                          <option value="">
                                            {a.list && a.list.length ? '-- เลือกครูที่ว่างคาบนี้ --' : 'ไม่มีครูว่างในคาบนี้'}
                                          </option>
                                          {(a.list || []).map(tc => (
                                            <option key={tc.t_id} value={String(tc.t_id)}>
                                              {tc.t_name}{tc.t_phone ? ` — ☎ ${tc.t_phone}` : ''}
                                            </option>
                                          ))}
                                        </Form.Select>
                                        <Form.Control
                                          size="sm"
                                          type="text"
                                          placeholder="เหตุผล/หมายเหตุ (ไม่บังคับ)"
                                          value={a.reason || ''}
                                          onChange={e => patchAssign(key, { reason: e.target.value })}
                                        />
                                        <div className="d-flex gap-2">
                                          <Button variant="primary" size="sm" disabled={a.saving} onClick={() => assignSubstitute(s)}>
                                            {a.saving ? 'กำลังบันทึก...' : 'มอบหมาย'}
                                          </Button>
                                          <Button variant="light" size="sm" onClick={() => patchAssign(key, { open: false })}>
                                            ปิด
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                        )}
                      </Card.Body>
                    )}
                  </Card>
                )
              })}
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
